import { create } from 'zustand'
import { siweAuth } from '@/lib/auth/siwe'
import type { AuthSession, BuyerProfileData, NeedsRegistration } from '@/types'

interface AuthStore {
  /** Current session after sign-in / registration */
  user: AuthSession | null
  isAuthenticated: boolean
  isAdmin: boolean
  isLoading: boolean

  /**
   * Starts the SIWE sign-in flow for a connected wallet.
   * Returns `NeedsRegistration` if the wallet has no account yet —
   * the caller should show ProfileForm and then call register().
   * Returns null on success (session already stored in cookie).
   */
  login: (address: string, chainId: number) => Promise<NeedsRegistration | null>

  /**
   * Completes registration for new users.
   * The pending address is already in the session cookie from /verify.
   */
  register: (profileData: BuyerProfileData) => Promise<void>

  /** Destroys the session cookie and clears local state. */
  logout: () => Promise<void>

  /**
   * Re-hydrates state from the session cookie on app boot.
   * Call this in a top-level effect (e.g. App.tsx).
   */
  checkSession: () => Promise<void>
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  isLoading: true,

  login: async (address, chainId) => {
    set({ isLoading: true })
    try {
      const result = await siweAuth.signIn(address, chainId)
      if ('needsRegistration' in result) {
        set({ isLoading: false })
        return result
      }
      // SIWE verification succeeded — session cookie is set by the server.
      // Refresh the local store state from the cookie.
      const session = await siweAuth.getSession()
      if (session) {
        set({
          user: session,
          isAuthenticated: true,
          isAdmin: session.isAdmin,
          isLoading: false,
        })
      } else {
        // Fallback: server returned null (cookie not sent or parsing failed)
        set({ isLoading: false })
      }
      return null
    } catch (err) {
      set({ isLoading: false })
      throw err
    }
  },

  register: async (profileData) => {
    set({ isLoading: true })
    try {
      const session = await siweAuth.register(profileData)
      set({ user: session, isAuthenticated: true, isAdmin: session.isAdmin, isLoading: false })
    } catch (err) {
      set({ isLoading: false })
      throw err
    }
  },

  logout: async () => {
    // Clear local state first so the user isn't stuck if the API call fails
    set({ user: null, isAuthenticated: false, isAdmin: false, isLoading: false })
    try { await siweAuth.signOut() } catch { /* cookie will expire on its own */ }
  },

  checkSession: async () => {
    set({ isLoading: true })
    try {
      const session = await siweAuth.getSession()
      set({
        user: session,
        isAuthenticated: !!session,
        isAdmin: session?.isAdmin ?? false,
        isLoading: false,
      })
    } catch {
      // API unreachable or failed — don't leave isLoading stuck
      set({ user: null, isAuthenticated: false, isAdmin: false, isLoading: false })
    }
  },
}))
